import React from 'react'
import { Bullet, Bullets, DocLink, Info, Page, Screenshot, Section } from '../../../components/CommandControlComponents'

const TreatmentCentres = () => (
  <Page
    title="Treatment Centres"
    intro="Treatment centres connect the control room to live patient flow. They summarise capacity and provide access to treatment-location workspaces."
  >
    <Section title="Control room summary">
      <Bullets>
        <Bullet>The Treatment Centres card shows event-wide treatment stats and active treatment locations.</Bullet>
        <Bullet>Stats are shown as small inline metrics for patients, waiting, welfare, and bed occupancy.</Bullet>
        <Bullet>Each treatment centre can be collapsed to show only the centre name and summary chips.</Bullet>
        <Bullet>Screen 1 shows each treatment centre on its own row for easier scanning.</Bullet>
      </Bullets>
      <Screenshot url="command-control/treatment-centres.png">
        Treatment Centres summary with inline stats and a collapsed treatment centre row showing only chips.
      </Screenshot>
    </Section>

    <Section title="Creating and configuring centres">
      <Bullets>
        <Bullet>Commanders can open the Treatment Centres manager from the control room.</Bullet>
        <Bullet>Centres can be created for medical centres, welfare areas, or other treatment locations.</Bullet>
        <Bullet>Assign staff to the centre and configure the operational zones or capacity.</Bullet>
        <Bullet>Locations can be opened from the control-room summary for detailed patient management.</Bullet>
      </Bullets>
      <Info>
        Who can do this? Creating a treatment centre from the control room is
        Commander-only. Assigning a patient to a treatment zone from the
        control room requires Dispatcher or Call Taker access, and zone
        capacity is enforced — assignments to a full zone are rejected.
      </Info>
    </Section>

    <Section title="Treatment-location workspace">
      <Bullets>
        <Bullet>Open <DocLink url="/commandcontrol/treatmentlocations" text="Treatment Locations" /> to access assigned live locations.</Bullet>
        <Bullet>Assigned staff can manage active patients, zones, transfers, and ePCR access.</Bullet>
        <Bullet>Commanders and site administrators can see all active treatment locations.</Bullet>
        <Bullet>Workstation lock and PIN switching are used where treatment locations are shared by multiple staff.</Bullet>
      </Bullets>
    </Section>

    <Section title="Treatment Workstations">
      <Bullets>
        <Bullet>A workstation is the clinician-facing shared-screen view of a treatment location, used to run a shared bed board at the centre itself, separate from the control-room summary.</Bullet>
        <Bullet>Open a location from <DocLink url="/commandcontrol/treatmentlocations" text="Treatment Locations" /> and use the "Open as Workstation" button in its header. The button is shown to Commanders and site administrators only.</Bullet>
        <Bullet>In workstation mode the board starts locked: staff sign in with their PIN, and inactivity locks the screen again automatically.</Bullet>
      </Bullets>
    </Section>

    <Section title="Mobile treatment workstation">
      <Bullets>
        <Bullet>The mobile app has a Treatment area: a directory of the treatment locations the signed-in user can enter (locations in active control rooms — Commanders and site administrators see them all).</Bullet>
        <Bullet>Each location opens a live bed board that polls for changes roughly every 2 seconds, so a tablet at the centre tracks the web workspace.</Bullet>
        <Bullet>The mobile board is board-only: zones and the treatment location itself are configured on the web.</Bullet>
        <Bullet>A location can be opened personally or as a shared-tablet workstation. In workstation mode, 120 seconds of inactivity or backgrounding the app locks the tablet, and staff unlock with their 4-digit PIN (with a password fallback).</Bullet>
        <Bullet>The workstation lock covers the whole session — including any ePCR opened from the board — so a shared tablet never sits open on a patient record.</Bullet>
      </Bullets>
    </Section>

    <Section title="Closing control rooms with treatment centres">
      <Bullets>
        <Bullet>Closing a control room closes its treatment locations.</Bullet>
        <Bullet>The system prevents closure while treatment locations still have active patients.</Bullet>
        <Bullet>Move, transfer, discharge, finalise, or delete active patient records before closing the control room.</Bullet>
      </Bullets>
      <Info>
        This protects against accidentally closing an event while patients remain under treatment-centre care.
      </Info>
    </Section>
  </Page>
)

export default TreatmentCentres
